/**
 * The capabilities History borrows from surfaces it may not import.
 *
 * Three of §3.9's History behaviours are owned by other feature folders, and
 * `03-ENGINEERING-SEAMS.md` §1.4 is explicit that a feature folder may never
 * import another one:
 *
 * - **F-HS-11** opens *the same manual-entry sheet as Train* — that sheet is the
 *   Train destination's (P28).
 * - **F-HS-08's Repeat** forwards a plan into a *new* workout, which the Build
 *   destination composes (P29).
 * - **F-HS-08's Edit effort** reopens the manual form against an existing
 *   record, again P28's.
 * - **F-HS-12** hands a recap to the Journal composer (P35).
 *
 * So they arrive as capabilities a host supplies, one context, defaulting to
 * "not composed here". That is a truthful default rather than a stub: a
 * capability that is genuinely absent makes its control absent too (F-HS-08
 * states Details appears "when an open handler exists", and the same reasoning
 * governs the rest), instead of rendering a control that would do nothing.
 *
 * The one exception is Details, which does not come through here at all: the
 * summary is presented through P41's own presentation context, which is the
 * sanctioned P41 → P40 edge.
 */

import type { ReactNode } from "react";
import { createContext, useContext } from "react";
import type { EffortSessionFields, SessionPlan } from "@ff/domain";
import type { JournalRepositoryPort } from "@ff/domain/ports";

import type { RecapOpener } from "../summary";
import type { DeferredRecapHandoff } from "./HistoryScreen";

export interface HistoryHost {
  /**
   * F-HS-11: opens the manual-entry sheet Train owns.
   *
   * The handoff travels with the call because F-HS-12's ordering is only
   * expressible from inside the sheet: it stashes a recap request while it is
   * open and forwards it once it has finished dismissing, and a host that
   * received no way to say either could not implement the deferral at all.
   */
  readonly addEffort?: (handoff: DeferredRecapHandoff) => void;
  /** F-HS-08: reopens that same sheet against an existing manual record. */
  readonly editEffort?: (session: EffortSessionFields) => void;
  /**
   * F-HS-08: builds a **new** workout from the plan this session ran. It never
   * mutates history, which is why the session travels beside the plan rather
   * than being looked up again from an id the builder would then hold.
   */
  readonly repeatPlan?: (plan: SessionPlan, session: EffortSessionFields) => void;
  /** F-HS-12: hands a stashed recap request to the Journal composer. */
  readonly openRecap?: RecapOpener;
  /**
   * F-HS-20, consulted before presenting a summary: whether a Journal entry or
   * draft already exists for the session. Absent means the surface reports
   * `false`, which is the safe direction — claiming a recap exists would replace
   * the quick reflection editor with a link to something the user cannot open.
   */
  readonly journal?: JournalRepositoryPort;
}

const EMPTY_HOST: HistoryHost = Object.freeze({});

const HistoryHostContext = createContext<HistoryHost>(EMPTY_HOST);

export interface HistoryHostProviderProps {
  readonly host: HistoryHost;
  readonly children?: ReactNode;
}

export function HistoryHostProvider({ host, children }: HistoryHostProviderProps): ReactNode {
  return <HistoryHostContext.Provider value={host}>{children}</HistoryHostContext.Provider>;
}

/**
 * Defaults to the empty host rather than throwing, unlike `usePorts()`. A
 * missing port is a composition bug; a missing *host capability* is the ordinary
 * state of this build, and History has to render honestly without it.
 */
export function useHistoryHost(): HistoryHost {
  return useContext(HistoryHostContext);
}
